import { useEffect, useState } from "react";
import { UserCheck, Trash2, ShieldCheck, Ban, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { auth, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

const PERMS = [
  { id: "overview", label: "Panoramica" }, { id: "channels", label: "Canali" }, { id: "roles", label: "Ruoli" },
  { id: "members", label: "Membri" }, { id: "mass_ops", label: "Operazioni massive" }, { id: "embed", label: "Embed Builder" },
  { id: "statistics", label: "Statistiche" }, { id: "backups", label: "Backup" }, { id: "settings", label: "Impostazioni" },
];

export default function Admissions() {
  const [users, setUsers] = useState(null);
  const [code, setCode] = useState("");
  const [codePerms, setCodePerms] = useState(["overview"]);
  const [busy, setBusy] = useState(false);

  const load = async () => { try { setUsers(await auth.users()); } catch (e) { toast.error(apiError(e)); setUsers([]); } };
  useEffect(() => { load(); }, []);

  const approve = async () => {
    if (!code.trim()) return toast.error("Inserisci il codice di ammissione");
    setBusy(true);
    try { await auth.approve(code.trim().toUpperCase(), codePerms); toast.success("Utente approvato"); setCode(""); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const togglePerm = (u, p) => {
    const perms = u.permissions?.includes(p) ? u.permissions.filter((x) => x !== p) : [...(u.permissions || []), p];
    auth.updateUser(u.id, { permissions: perms }).then(() => { toast.success("Permessi aggiornati"); load(); }).catch((e) => toast.error(apiError(e)));
  };
  const setStatus = (u, status) => auth.updateUser(u.id, { status }).then(() => { toast.success("Aggiornato"); load(); }).catch((e) => toast.error(apiError(e)));
  const del = (u) => auth.deleteUser(u.id).then(() => { toast.success("Eliminato"); load(); });

  const pending = (users || []).filter((u) => u.status === "pending");
  const approved = (users || []).filter((u) => u.status !== "pending");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-white sm:text-3xl">Ammissioni</h1>
        <p className="text-sm text-slate-400">Approva i collaboratori e assegna i permessi di accesso alla dashboard.</p>
      </div>

      <div className="card-elevated p-5">
        <h2 className="mb-3 flex items-center gap-2 font-bold text-white"><UserCheck className="h-5 w-5 text-[#5865F2]" />Approva con codice</h2>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1"><label className="mb-1 block text-xs font-bold uppercase text-slate-500">Codice di ammissione</label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="ES: A1B2C3D4" data-testid="admission-code-input" className="rounded-xl border-white/10 bg-[#0B0C10] font-mono text-white" /></div>
          <Button onClick={approve} disabled={busy} data-testid="admission-approve-button" className="bg-[#5865F2] hover:bg-[#4752C4]">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}Approva</Button>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {PERMS.map((p) => {
            const on = codePerms.includes(p.id);
            return <button key={p.id} onClick={() => setCodePerms(on ? codePerms.filter((x) => x !== p.id) : [...codePerms, p.id])}
              className={`rounded-lg border px-2.5 py-1 text-xs font-semibold ${on ? "border-[#5865F2] bg-[#5865F2]/15 text-white" : "border-white/10 text-slate-400 hover:text-white"}`}>{p.label}</button>;
          })}
        </div>
      </div>

      {pending.length > 0 && (
        <div className="card-elevated p-5">
          <h2 className="mb-3 font-bold text-[#FEE75C]">In attesa ({pending.length})</h2>
          <div className="space-y-2">
            {pending.map((u) => (
              <div key={u.id} className="flex items-center gap-3 rounded-xl border border-white/5 bg-[#0B0C10] px-3 py-2.5">
                <div className="flex-1"><p className="font-semibold text-white">{u.displayName}</p><p className="text-xs text-slate-500">{u.email}</p></div>
                <span className="font-mono text-sm text-[#FEE75C]">{u.approvalCode}</span>
                <Button size="sm" onClick={() => setCode(u.approvalCode)} className="bg-white/5 text-white hover:bg-white/10">Usa codice</Button>
                <Button size="icon" variant="ghost" onClick={() => del(u)} className="text-[#F23F43]"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card-elevated p-5" data-testid="users-list">
        <h2 className="mb-3 font-bold text-white">Collaboratori</h2>
        {users === null && <div className="space-y-2">{[0,1].map(i=><Skeleton key={i} className="h-16 rounded-lg bg-white/5" />)}</div>}
        {users && approved.length === 0 && <p className="text-sm text-slate-500">Nessun collaboratore approvato.</p>}
        <div className="space-y-3">
          {approved.map((u) => (
            <div key={u.id} className="rounded-xl border border-white/5 bg-[#0B0C10] p-3">
              <div className="mb-2 flex items-center gap-3">
                <div className="flex-1"><p className="font-semibold text-white">{u.displayName} {u.role === "owner" && <span className="rounded bg-[#5865F2] px-1 text-[10px]">OWNER</span>}</p><p className="text-xs text-slate-500">{u.email}</p></div>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${u.status === "approved" ? "bg-[#23A55A]/15 text-[#23A55A]" : "bg-[#F23F43]/15 text-[#F23F43]"}`}>{u.status}</span>
                {u.status === "approved" ? <Button size="sm" variant="ghost" onClick={() => setStatus(u, "disabled")} className="text-[#F0B232]"><Ban className="mr-1 h-4 w-4" />Disabilita</Button>
                  : <Button size="sm" variant="ghost" onClick={() => setStatus(u, "approved")} className="text-[#23A55A]">Riattiva</Button>}
                <Button size="icon" variant="ghost" onClick={() => del(u)} className="text-[#F23F43]"><Trash2 className="h-4 w-4" /></Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {PERMS.map((p) => {
                  const on = u.permissions?.includes(p.id);
                  return <button key={p.id} onClick={() => togglePerm(u, p.id)} className={`rounded-lg border px-2 py-0.5 text-[11px] font-semibold ${on ? "border-[#5865F2] bg-[#5865F2]/15 text-white" : "border-white/10 text-slate-500 hover:text-white"}`}>{p.label}</button>;
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
